import React, { useState } from 'react';
import styled from 'styled-components';
import { FaFileAlt, FaUser } from "react-icons/fa";

function GoogleDocPreview({ previewUrl }) {
    const [previewFailed, setPreviewFailed] = useState(false);

    return (
        <Section>
            <Label>Document Preview</Label>
            {previewFailed ? (
                <FallbackBox>
                    ⚠️ Failed to load document preview.
                    <br />
                    <a href={previewUrl} target="_blank" rel="noopener noreferrer">
                        Open document in new tab
                    </a>
                </FallbackBox>
            ) : (
                <StyledIframe
                    src={previewUrl}
                    title="Google Doc Preview"
                    width="100%"
                    height="600"
                    allow="autoplay; fullscreen"
                    allowFullScreen
                    onError={(e) => {
                        console.warn('Preview failed to load:', e);
                        setPreviewFailed(true);
                    }}
                />
            )}
        </Section>
    );
}

const GoogleDocPopupCard = ({ meta, groupMembers, onClose }) => {
    if (!meta || Object.keys(meta).length === 0) return null;

    return (
        <ContentWrapper>
            <CloseButton onClick={onClose}>✕</CloseButton>
            <Card>
                <TitleRow>
                    <Label>Google Doc Title</Label>
                </TitleRow>
                <Header>{meta.title}</Header>
                <Section>
                    <Label>Google Doc Metadata</Label>
                    <MetaGrid>
                        {/* Word Count */}
                        <MetaBox color={{ bg: "#eff6ff", border: "#bfdbfe", text: "#3b82f6", value: "#1d4ed8" }}>
                            <IconWrapper><FaFileAlt size={24} /></IconWrapper>
                            <MetaText color={{ text: "#3b82f6", value: "#1d4ed8" }}>
                                <p>Total Word Count</p>
                                <p>{meta.wordCount}</p>
                            </MetaText>
                        </MetaBox>

                        {/* Word Change */}
                        <MetaBox color={{ bg: "#fef2f2", border: "#fecaca", text: "#dc2626", value: "#b91c1c" }}>
                            <IconWrapper><FaUser size={24} /></IconWrapper>
                            <MetaText color={{ text: "#dc2626", value: "#b91c1c" }} small={!meta.wordChange || (meta.wordChange.added === 0 && meta.wordChange.removed === 0)}>
                                {meta.wordChange ? (
                                    meta.wordChange.added === 0 && meta.wordChange.removed === 0 ? (
                                        <>
                                            <p>Word Changes</p>
                                            <p>Same Document</p>
                                        </>
                                    ) : (
                                        <>
                                            <p>Word Changes</p>
                                            <CodeChangeLine>
                                                <Green>+{meta.wordChange.added}</Green>
                                                <Divider>/</Divider>
                                                <Red>-{meta.wordChange.removed}</Red>
                                            </CodeChangeLine>
                                        </>
                                    )
                                ) : (
                                    <>
                                        <p>Initial Version</p>
                                        <p>First submission</p>
                                    </>
                                )}
                            </MetaText>
                        </MetaBox>

                    </MetaGrid>
                </Section>
                {(meta.pdfStorage?.url || meta.previewUrl) && (
                  <GoogleDocPreview
                    previewUrl={meta.pdfStorage?.url || meta.previewUrl}
                    isPdf={!!meta.pdfStorage?.url}
                  />
                )}
                <ButtonSection>
                    {meta.pdfStorage?.url && (
                        <ViewButton href={meta.pdfStorage.url} target="_blank" rel="noopener noreferrer">
                            View PDF
                        </ViewButton>
                    )}
                    {meta.link && (
                        <ViewButton href={meta.link} target="_blank" rel="noopener noreferrer">
                            Go to Google Doc
                        </ViewButton>
                    )}
                </ButtonSection>
            </Card>
        </ContentWrapper>
    );
};

export default GoogleDocPopupCard;

const ContentWrapper = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    padding-top: 15px;
    padding-bottom: 15px;
    align-items: flex-start;
`;

const CloseButton = styled.button`
  position: absolute;
  top: -8px;     /* Push above the card */
  right: 0;      
  background: transparent;
  border: none;
  font-size: 1.8rem;
  font-weight: 700;
  color: #6b7280;
  cursor: pointer;

  &:hover {
    color: #111827;
  }
`;

// Doc Meta Card
const Card = styled.div`
  background: white;
  width: 90%;
  padding: 2rem 2rem 1rem 2rem;
  border-radius: 0.75rem;
  box-shadow: 0 2px 10px rgba(0,0,0,0.08);
  margin: 2rem auto;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
  background-color: #ffffff; 
  text-align: left;

  border: 1px solid #e5e7eb;
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.25rem;
`;

const Header = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
`;

const Label = styled.small`
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280; /* Tailwind gray-500 */
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;


//Meta data
const Section = styled.div`
  margin-bottom: 1.5rem;
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const MetaBox = styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== 'full',
})`
  display: flex;
  align-items: center;
  background: ${({ color }) => color.bg};
  border: 1px solid ${({ color }) => color.border};
  padding: 1rem;
  border-radius: 0.5rem;
  grid-column: ${({ full }) => (full ? "span 2" : "auto")};
`;

const IconWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
`;

const MetaText = styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== 'color' && prop !== 'small',
})`
  p:first-child {
    font-size: ${({ small }) => (small ? '0.875rem' : '0.875rem')};
    color: ${({ color }) => color.text};
    font-weight: 500;
  }

  p:last-child {
    font-size: ${({ small }) => (small ? '0.95rem' : '1.5rem')};
    font-weight: 700;
    color: ${({ color }) => color.value};
  }
`;


const FallbackBox = styled.div`
  padding: 1rem;
  background-color: #fef2f2;
  color: #991b1b;
  border: 1px solid #fca5a5;
  border-radius: 0.5rem;
  text-align: center;
  font-size: 0.95rem;

  a {
    color: #2563eb;
    text-decoration: underline;
  }
`;

const StyledIframe = styled.iframe`
  width: 100%;
  height: 600px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  background-color: white;
`;


const CodeChangeLine = styled.p`
  font-size: 1.5rem;
  font-weight: 700;
  display: flex;
  align-items: baseline;
  gap: 0.25rem;
`;

const Green = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
  color: #16a34a;
`;

const Red = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
  color: #dc2626;
`;

const Divider = styled.span`
  color: #6d28d9; /* match purple if needed */
  padding: 0 0.2rem; /* adjust spacing */
  font-weight: 700;
`;

const ButtonSection = styled.div`
  display: flex;
  gap: 1rem;
  margin: 1.5rem 0 1.2rem 0;
  flex-wrap: wrap;
  justify-content: center;
`;

const ViewButton = styled.a`
  display: inline-block;
  padding: 0.75rem 1.5rem;
  background-color: #0b99ff;
  color: white;
  text-decoration: none;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  transition: background-color 0.2s ease;
  cursor: pointer;

  &:hover {
    background-color: #0a7fd9;
    scale: 1.02;
  }

  &:active {
    background-color: #0966b3;
  }
`;



